import { useState } from 'react';

import { Button } from '../../button';

import {
  barLayout,
  CALLS,
  CHAIN_NOTES,
  CHAIN_REFINEMENT,
  chainNoteLabels,
  chainNoteTexts,
  chainSteps,
  chainSummary,
  chainTransition,
  CHECK_PROPOSALS,
  CHECK_REFINEMENT,
  checkProposalLabels,
  checkProposals,
  checkSummary,
  checkTransition,
  codeVerdictText,
  droppedLines,
  EXISTING_TASKS,
  initialChain,
  initialCheck,
  initialParallel,
  initialRoute,
  judgeText,
  nextSteps,
  PARALLEL_MODES,
  PARALLEL_REFINEMENT,
  parallelModeLabels,
  parallelSummary,
  parallelTransition,
  proposalJson,
  retryLabel,
  ROUTE_MESSAGES,
  ROUTE_REFINEMENT,
  routedMessages,
  routeMessageLabels,
  routeSummary,
  routeTransition,
  timelineBars,
  toChainNote,
  toCheckProposal,
  tokensLine,
  toParallelMode,
  toRouteMessage,
  totalSeconds,
  type ChainAction,
  type CheckAction,
  type ParallelAction,
  type RouteAction,
} from './flow-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './relationship-labs.css';
import './flow-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="rel-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}
function Table({
  label,
  columns,
  rows,
  template = `repeat(${columns.length}, minmax(0, 1fr))`,
}: {
  label: string;
  columns: string[];
  rows: (string | number)[][];
  template?: string;
}) {
  return (
    <div className="rel-table-scroll">
      <div className="rel-table cb-record-grid" role="table" aria-label={label}>
        <div
          role="row"
          className="rel-row rel-table-head"
          style={{ gridTemplateColumns: template }}
        >
          {columns.map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {rows.length ? (
          rows.map((row, index) => (
            <div
              role="row"
              className="rel-row"
              key={index}
              style={{ gridTemplateColumns: template }}
            >
              {row.map((cell, column) => (
                <span role="cell" key={column}>
                  {cell}
                </span>
              ))}
            </div>
          ))
        ) : (
          <div role="row" className="rel-row">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}
function Facts({ values }: { values: [string, string][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
const bullets = (titles: readonly string[]) =>
  titles.length ? titles.map((title) => `- ${title}`).join('\n') : '(none)';
const NOT_RUN = '(not run yet)';
const REPLAY = 'This graphic replays real runs.';

export function FlowRouteLab() {
  const [state, setState] = useState(initialRoute);
  const act = (action: RouteAction) => setState((current) => routeTransition(current, action));
  const message = routedMessages[state.message];
  const summary = routeSummary(state);
  return (
    <Lab
      title="Which label does the router give, and what runs next?"
      eyebrow="Workflows · the router"
      className="rel-lab flow-lab"
    >
      <Predict>Which label does the router give? Choose a message, then press Classify.</Predict>
      <div className="cb-controls">
        <label>
          Message
          <ModelSelect
            aria-label="Message"
            value={state.message}
            onValueChange={(value) => act({ type: 'message', value: toRouteMessage(value) })}
            options={ROUTE_MESSAGES.map((key) => ({ value: key, label: routeMessageLabels[key] }))}
          />
        </label>
        <Button size="sm" disabled={state.classified} onClick={() => act({ type: 'classify' })}>
          Classify
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The message">
        <pre aria-label="The message">{message.text}</pre>
      </Panel>
      {state.classified ? (
        <Facts
          values={[
            ['Label', message.label],
            ['Reason', message.reason],
            ['What runs next', nextSteps[message.label]],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.classified && state.message === 'mixed' ? (
        <p className="rel-caption">{ROUTE_REFINEMENT}</p>
      ) : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function FlowChainLab() {
  const [state, setState] = useState(initialChain);
  const act = (action: ChainAction) => setState((current) => chainTransition(current, action));
  const run = state.run;
  const summary = chainSummary(state);
  return (
    <Lab
      title="How many tasks does the chain propose, and which step removes the duplicate?"
      eyebrow="Workflows · the note chain"
      className="rel-lab flow-lab"
    >
      <Predict>
        How many tasks does the chain propose? Choose a note, then press Run the chain.
      </Predict>
      <div className="cb-controls">
        <label>
          Note
          <ModelSelect
            aria-label="Note"
            value={state.note}
            onValueChange={(value) => act({ type: 'note', value: toChainNote(value) })}
            options={CHAIN_NOTES.map((note) => ({ value: note, label: chainNoteLabels[note] }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.compareOn}
          onClick={() => act({ type: 'toggle-compare' })}
        >
          Compare with existing tasks in code
        </Button>
        <Button size="sm" disabled={run !== null} onClick={() => act({ type: 'run' })}>
          Run the chain
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The note">
        <pre aria-label="The note">{chainNoteTexts[state.note]}</pre>
      </Panel>
      <Table
        label="Steps"
        columns={['Step', 'Who', 'Input', 'Output']}
        template="minmax(0, 0.6fr) minmax(0, 0.6fr) minmax(0, 1.2fr) minmax(0, 2fr)"
        rows={
          run
            ? chainSteps(run, state.compareOn).map((step) => [
                step.step,
                step.who,
                step.input,
                step.output,
              ])
            : []
        }
      />
      <div className="cb-two">
        <Panel label="Dropped as duplicates">
          <pre aria-label="Dropped as duplicates">{run ? droppedLines(run) : NOT_RUN}</pre>
        </Panel>
        <Panel label="Proposals">
          <pre aria-label="Proposals">{run ? bullets(run.proposals) : NOT_RUN}</pre>
        </Panel>
      </div>
      {run ? (
        <Facts
          values={[
            ['Extracted', String(run.extracted.length)],
            ['Dropped', String(run.dropped.length)],
            ['Proposed', String(run.proposals.length)],
          ]}
        />
      ) : null}
      <div className="cb-controls cb-secondary">
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.showExisting}
          onClick={() => act({ type: 'toggle-existing' })}
        >
          Show the existing tasks
        </Button>
      </div>
      {state.showExisting ? (
        <Panel label="Existing tasks">
          <pre aria-label="Existing tasks">{bullets(EXISTING_TASKS)}</pre>
        </Panel>
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{CHAIN_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function FlowParallelLab() {
  const [state, setState] = useState(initialParallel);
  const act = (action: ParallelAction) =>
    setState((current) => parallelTransition(current, action));
  const summary = parallelSummary(state);
  return (
    <Lab
      title="How long does the inbox take when two calls run together?"
      eyebrow="Workflows · two threads"
      className="rel-lab flow-lab"
    >
      <Predict>
        How long do two calls take together? Choose Together in Run the digest and the proposals.
      </Predict>
      <div className="cb-controls">
        <label>
          Run the digest and the proposals
          <ModelSelect
            aria-label="Run the digest and the proposals"
            value={state.mode}
            onValueChange={(value) => act({ type: 'mode', value: toParallelMode(value) })}
            options={PARALLEL_MODES.map((mode) => ({
              value: mode,
              label: parallelModeLabels[mode],
            }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.compareOn}
          onClick={() => act({ type: 'toggle-compare' })}
        >
          Also run compare together
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <div className="flow-timeline" role="list" aria-label="Timeline">
        {timelineBars(state).map((bar) => (
          <div role="listitem" key={bar.name}>
            <span>
              {bar.name}, {bar.text}
            </span>
            <div aria-hidden="true">
              <div className="flow-bar" style={barLayout(bar)} />
            </div>
          </div>
        ))}
      </div>
      <Facts
        values={[
          ['Total time', `${totalSeconds(state)} s`],
          ['Tokens', tokensLine],
          ['Calls', String(CALLS)],
        ]}
      />
      <Result tone={summary.tone}>{summary.message}</Result>
      {state.compareOn ? <p className="rel-caption">{PARALLEL_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}

export function FlowCheckLab() {
  const [state, setState] = useState(initialCheck);
  const act = (action: CheckAction) => setState((current) => checkTransition(current, action));
  const run = state.run;
  const proposal = checkProposals[state.proposal];
  const summary = checkSummary(state);
  return (
    <Lab
      title="Does the checker catch the bad proposal, and which checker?"
      eyebrow="Workflows · the checker"
      className="rel-lab flow-lab"
    >
      <Predict>Which checker stops the bad proposal? Choose a proposal, then press Check.</Predict>
      <div className="cb-controls">
        <label>
          Proposal
          <ModelSelect
            aria-label="Proposal"
            value={state.proposal}
            onValueChange={(value) => act({ type: 'proposal', value: toCheckProposal(value) })}
            options={CHECK_PROPOSALS.map((key) => ({
              value: key,
              label: checkProposalLabels[key],
            }))}
          />
        </label>
        <Button
          size="sm"
          variant="secondary"
          aria-pressed={state.codeFirst}
          onClick={() => act({ type: 'toggle-code-first' })}
        >
          Code checks first
        </Button>
        <Button size="sm" disabled={run !== null} onClick={() => act({ type: 'check' })}>
          Check
        </Button>
        <Reset onClick={() => act({ type: 'reset' })} />
      </div>
      <Panel label="The proposal">
        <pre aria-label="The proposal">{proposalJson(proposal)}</pre>
      </Panel>
      <div className="cb-two">
        <Panel label="Code checks">
          <pre aria-label="Code checks">
            {run ? codeVerdictText(run.code, proposal) : '(not checked yet)'}
          </pre>
        </Panel>
        <Panel label="Judge">
          <pre aria-label="Judge">{run ? judgeText(run.judge) : '(not checked yet)'}</pre>
        </Panel>
      </div>
      {run ? (
        <Facts
          values={[
            ['Judge calls', String(run.judgeCalls)],
            ['Retry', retryLabel(run.retried)],
            ['Outcome', run.outcome],
          ]}
        />
      ) : null}
      <Result tone={summary.tone}>{summary.message}</Result>
      {summary.tone === 'warning' ? <p className="rel-caption">{CHECK_REFINEMENT}</p> : null}
      <p className="rel-caption">{REPLAY}</p>
    </Lab>
  );
}
